import React, { useCallback, useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import { Download, Loader2, CalendarDays } from 'lucide-react';
import dayjs from 'dayjs';
import { adminApi, adminErrorMsg } from '../../admin/adminApiClient';
import { toast } from 'sonner';

export default function AdminReportsPage() {
  const [from, setFrom] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
  const [to, setTo] = useState(dayjs().format('YYYY-MM-DD'));
  const [daily, setDaily] = useState([]);
  const [topOrgs, setTopOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [enqR, orgR] = await Promise.all([
        adminApi.get('/reports/enquiries', { params: { from_: from, to } }),
        adminApi.get('/reports/organizers/top', { params: { limit: 10 } }),
      ]);
      setDaily(enqR.data?.daily || []);
      setTopOrgs(orgR.data?.items || orgR.data || []);
    } catch (e) { toast.error(adminErrorMsg(e)); }
  }, [from, to]);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  const exportCSV = () => {
    if (!topOrgs.length) return;
    const header = 'Rank,Organizer,Enquiries\n';
    const rows = topOrgs.map((o, i) => `${i + 1},"${o.organizer_name}",${o.enquiries}`).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `top_organizers_${dayjs().format('YYYYMMDD')}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-reports-page">
      <h1 className="text-xl font-black text-gray-900 mb-4">Reports</h1>

      {/* Date Range */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
          <CalendarDays className="w-4 h-4 text-gray-400" />
          <input data-testid="report-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="text-sm font-semibold text-gray-700 bg-transparent focus:outline-none" />
        </div>
        <span className="text-gray-400 text-sm font-bold">→</span>
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
          <CalendarDays className="w-4 h-4 text-gray-400" />
          <input data-testid="report-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="text-sm font-semibold text-gray-700 bg-transparent focus:outline-none" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-magenta" /></div>
      ) : (
        <>
          {/* Daily Enquiries Chart */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6" data-testid="report-daily-chart">
            <h2 className="text-sm font-bold text-gray-900 mb-3">Daily Enquiries</h2>
            {daily.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => dayjs(d).format('M/D')} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip labelFormatter={(d) => dayjs(d).format('ddd, MMM D')} />
                  <Line type="monotone" dataKey="count" stroke="#E91E63" strokeWidth={2} dot={{ r: 3, fill: '#E91E63' }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-400 text-center py-10">No data for this range</p>
            )}
          </div>

          {/* Top Organizers */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="report-top-orgs">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-bold text-gray-900">Top Organizers by Enquiries</h2>
              <button data-testid="report-export-csv" onClick={exportCSV} className="flex items-center gap-1 text-brand-magenta text-xs font-bold hover:text-pink-700">
                <Download className="w-3 h-3" /> Export CSV
              </button>
            </div>
            {topOrgs.length ? (
              <>
                <ResponsiveContainer width="100%" height={200} className="px-4 pt-3">
                  <BarChart data={topOrgs}>
                    <XAxis dataKey="organizer_name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="enquiries" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="top-orgs-table">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left px-4 py-2 text-[11px] font-bold text-gray-500 uppercase">#</th>
                        <th className="text-left px-4 py-2 text-[11px] font-bold text-gray-500 uppercase">Organizer</th>
                        <th className="text-right px-4 py-2 text-[11px] font-bold text-gray-500 uppercase">Enquiries</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {topOrgs.map((o, i) => (
                        <tr key={o.organizer_id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-500 font-bold">{i + 1}</td>
                          <td className="px-4 py-2 font-semibold text-gray-900">{o.organizer_name}</td>
                          <td className="px-4 py-2 text-right">
                            <span className="bg-pink-50 text-pink-700 text-xs font-black px-2 py-0.5 rounded-full">{o.enquiries}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-400 text-center py-10">No organizer data</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
